import React, { useEffect, useState, useCallback } from 'react';
import { 
  RefreshCw, 
  Download, 
  Eye, 
  CheckCircle2, 
  XCircle, 
  Clock,
  FileText,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/utils/api';
import type { FileRecord, FileDetails } from '@/types';
import { useWebSocket } from '@/hooks/useWebSocket';

const Files: React.FC = () => {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<FileDetails | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const loadFiles = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const response = await api.listFiles();
      setFiles(response.files);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // WebSocket for real-time status updates
  useWebSocket({
    onFileStatusUpdate: () => {
      // Refresh files list when we receive an update
      loadFiles(false); // Silent refresh
    },
  });

  const viewFileDetails = async (fileId: string) => {
    try {
      const details = await api.getFile(fileId);
      setSelectedFile(details);
      setDialogOpen(true);
    } catch (err) {
      console.error('Failed to load file details:', err);
    }
  };

  const downloadFile = async (fileId: string) => {
    try {
      setDownloadingFileId(fileId);
      const details = await api.getFile(fileId);
      if (details.downloadUrl) {
        window.open(details.downloadUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to download file:', err);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const getStatusIcon = (status: FileRecord['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'FAILED':
      case 'TIMEOUT':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-amber-500" />;
    }
  };

  const getStatusBadge = (status: FileRecord['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'FAILED':
        return <Badge variant="destructive">Failed</Badge>;
      case 'TIMEOUT':
        return <Badge variant="destructive">Timed out</Badge>;
      case 'PENDING':
      case 'UPLOADING':
      case 'QUEUED':
      case 'PROCESSING':
        return <Badge variant="secondary">Processing</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Files</h1>
          <p className="text-muted-foreground mt-1">
            Manage your uploaded and processed files
          </p>
        </div>
        <Button variant="outline" onClick={() => loadFiles(true)} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 border border-destructive/50 bg-destructive/10 rounded-lg text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No files yet. Upload your first file to get started.</p>
            </div>
          ) : (
            <div className="divide-y">
              {files.map((file) => (
                <div
                  key={file.fileId}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                >
                  {getStatusIcon(file.status)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.originalFilename}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{formatFileSize(file.fileSize)}</span>
                      <span>{formatDate(file.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(file.status)}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => viewFileDetails(file.fileId)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => downloadFile(file.fileId)}
                      disabled={file.status !== 'COMPLETED' || downloadingFileId === file.fileId}
                    >
                      {downloadingFileId === file.fileId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* File Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>File Details</DialogTitle>
            <DialogDescription>
              Detailed information about the file and processing status
            </DialogDescription>
          </DialogHeader>
          {selectedFile && (
            <div className="space-y-6">
              <div className="space-y-2">
                <p className="text-sm font-medium">Filename</p>
                <p className="text-sm text-muted-foreground">
                  {selectedFile.file.originalFilename}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Status</p>
                  {getStatusBadge(selectedFile.file.status)}
                </div>
                <div>
                  <p className="text-sm font-medium">Size</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(selectedFile.file.fileSize)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Created</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(selectedFile.file.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Type</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedFile.file.mimeType}
                  </p>
                </div>
              </div>
              {selectedFile.file.errorMessage && (
                <div className="p-4 border border-destructive/50 bg-destructive/10 rounded-lg">
                  <p className="text-sm font-medium text-destructive">Error</p>
                  <p className="text-sm text-destructive">
                    {selectedFile.file.errorMessage}
                  </p>
                </div>
              )}
              {selectedFile.jobs.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Processing Jobs</p>
                  <div className="space-y-2">
                    {selectedFile.jobs.map((job: any) => (
                      <div
                        key={job.jobId}
                        className="p-3 border rounded-lg text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{job.jobId}</span>
                          <Badge variant={job.status === 'COMPLETED' ? 'default' : 'secondary'}>
                            {job.status}
                          </Badge>
                        </div>
                        {job.errorMessage && (
                          <p className="text-destructive mt-1">{job.errorMessage}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedFile.downloadUrl && (
                <Button
                  className="w-full"
                  onClick={() => window.open(selectedFile.downloadUrl!, '_blank')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Sanitized File
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Files;
